import Field from '../field'
import { query } from '../api/query'
import { getBooking } from '../api/getBooking'
import { getMasterBookingExtras } from '../api/getMasterBookingExtras'
import { formatMoney } from '../helpers/formatMoney'
import {
    applySummaryBookingDetails,
    applySummaryExtras,
    applySummaryPrice,
} from '../helpers/updateBookingSummary'
import { trackExtrasQuantityChange } from '../../analytics/holidayParkDataLayer'
import { showExtrasSummaryLoading, hideExtrasSummaryLoading } from '../helpers/extrasSummaryLoading'
import { showExtrasLoading, hideExtrasLoading } from '../helpers/extrasLoading'

class Extras {
    fields = []
    stage = 2

    constructor(form, bookingNo, bookingSummary) {
        this.extrasForm = form
        this.bookingNo = bookingNo
        this.masterExtras = this.getMasterBookingExtras()
        this.bookingSummary = bookingSummary
        this.extras = []
        this.categoryExpandedState = new Map()
        this.bindCatalogElements()

        this.initializeRenderedCatalog()
    }


    async getMasterBookingExtras() {
        return await getMasterBookingExtras()
    }

    async update(currentStage) {
        if (currentStage == this.stage) {
            return []
        }
    }

    async onLoad(currentStage) {
        if (currentStage == this.stage) {
            if (!this.extrasWrap && !this.catalogContainer) {
                return
            }

            this.loading()
            this.hideError()

            try {
                this.booking = await getBooking(this.bookingNo)
                this.mergeExtras(this.booking.extras ?? [])
                this.applyExtraState()
                const bookingSummaryResponse = this.booking

                this.applyFiltersAndSorting()
                this.applyBookingSummary(bookingSummaryResponse)
            } catch (error) {
                this.showError()
                console.error('Failed to load extras:', error.message)
            } finally {
                this.stopLoading()
            }
        }
    }

    loading() {
        showExtrasLoading()
        if (this.catalogContainer) {
            this.catalogContainer.classList.add('hidden')
        }
    }

    stopLoading() {
        if (this.catalogContainer) {
            this.catalogContainer.classList.remove('hidden')
        }
        hideExtrasLoading()
    }

    createFields() {
        this.fields = []
        this.extraInputs.forEach((input) => {
            this.fields.push(new Field(input.name, this.extrasForm, false))
        })
    }

    initializeExtrasState() {
        this.extras = this.fields.map((field) => ({
            code: field.name,
            quantity: this.getFieldQuantity(field.htmlElement),
        }))
    }

    applyExtraState() {
        this.fields.forEach(field => {
            const extra = this.extras.find(extra => field.name === extra.code);
            const optionElement = field.htmlElement.closest('.option')
            const priceField = optionElement?.querySelector('.price')
            const defaultPrice = priceField?.dataset?.defaultPrice ?? ''
            const defaultQuantity = String(field.htmlElement.dataset.defaultQuantity ?? '0')
            const displayPrice = extra?.unit_price ?? defaultPrice

            if (displayPrice && priceField) {
                priceField.textContent = formatMoney(displayPrice)
            } else if (priceField) {
                priceField.textContent = ''
            }

            if (extra) {
                if (priceField) {
                    priceField.textContent = formatMoney(displayPrice)
                }

                if (field.htmlElement.tagName == "INPUT") {
                    field.htmlElement.checked = extra.quantity > 0
                }
                else if (field.htmlElement.tagName == "SELECT") {
                    field.htmlElement.value = String(extra.quantity)
                }
            } else {
                if (field.htmlElement.tagName == "INPUT") {
                    field.htmlElement.checked = false
                } else if (field.htmlElement.tagName == "SELECT") {
                    field.htmlElement.value = defaultQuantity
                }
            }

        });
    }

    addEventListenersToInputs() {
        this.extraInputs.forEach(input => {
            input.addEventListener("change", async (event) => {
                showExtrasSummaryLoading()
                const previousExtra = { ...(this.extras.find((extra) => extra.code === event.target.dataset.code) ?? {}) }
                const value = event.target.tagName === "INPUT"
                    ? (event.target.checked ? "1" : "0")
                    : event.target.value

                this.updateSelectedExtras(event.target.dataset.code, value)

                try {
                    const response = await this.updateExtras()
                    this.applyBookingSummary(response)
                    trackExtrasQuantityChange({
                        beforeExtra: previousExtra,
                        afterExtra: this.extras.find((extra) => extra.code === event.target.dataset.code) ?? null,
                    })
                } catch (error) {
                    this.showError()
                    console.error('Failed to update extras:', error.message)
                } finally {
                    hideExtrasSummaryLoading()
                }
            })
        })
    }

    updateSelectedExtras(code, quantity) {
        const newExtra = {
            code: code,
            quantity: quantity
        }

        const existingExtraIndex = this.extras.findIndex(extra => extra.code == newExtra.code);
        if (existingExtraIndex !== -1) {
            this.extras[existingExtraIndex].quantity = newExtra.quantity;
        } else {
            this.extras.push(newExtra);
        }
    }

    async updateExtras() {
        const URL = this.extrasForm.dataset.update_extras_route
        const response = await query(URL, this.bookingNo, this.buildRequestPayload())
        const extras = response.extrasWithPrices ?? []
        this.mergeExtras(extras)
        this.applyExtraState()
        this.applyFiltersAndSorting()
        return response
    }

    mergeExtras(extras) {
        extras.forEach((extra) => {
            const existingExtraIndex = this.extras.findIndex((existingExtra) => existingExtra.code === extra.code)

            if (existingExtraIndex !== -1) {
                this.extras[existingExtraIndex] = {
                    ...this.extras[existingExtraIndex],
                    ...extra,
                    quantity: String(extra.quantity ?? this.extras[existingExtraIndex].quantity ?? '0'),
                }
            } else {
                this.extras.push(extra)
            }
        })
    }

    buildRequestPayload() {
        return this.extras.map((extra) => ({
            code: extra.code,
            quantity: String(extra.quantity ?? '0'),
        }))
    }

    getFieldQuantity(fieldElement) {
        if (fieldElement.tagName === 'INPUT') {
            return fieldElement.checked ? String(fieldElement.value || '1') : '0'
        }

        return String(fieldElement.value ?? '0')
    }

    showError() {
        if (this.errorMessage) {
            this.errorMessage.classList.remove('hidden')
        }
    }

    hideError() {
        if (this.errorMessage) {
            this.errorMessage.classList.add('hidden')
        }
    }

    bindCatalogElements() {
        this.catalogContainer = this.extrasForm.querySelector('[data-extras-catalog-container]')
        this.catalogElement = this.extrasForm.querySelector('[data-extras-catalog]')
        this.extrasWrap = this.extrasForm.querySelector('#extras-wrap')
        this.errorMessage = this.extrasForm.querySelector('#extras-error-message')
        this.searchInput = this.extrasForm.querySelector('[data-extras-search]')
        this.sortSelect = this.extrasForm.querySelector('[data-extras-sort]')
        this.noResultsMessage = this.extrasForm.querySelector('[data-extras-empty-state]')
        this.categorySections = Array.from(this.extrasForm.querySelectorAll('[data-extras-category]'))
        this.extraInputs = Array.from(this.extrasForm.querySelectorAll('[data-extra-input]'))
        this.descriptionElement = this.extrasForm.querySelector('[data-extras-description]')
        this.stageEmptyState = this.extrasForm.querySelector('[data-extras-stage-empty-state]')
    }

    initializeRenderedCatalog() {
        this.bindCatalogElements()
        this.categoryExpandedState = new Map()

        if (this.extraInputs.length === 0) {
            return
        }

        this.createFields()
        this.initializeExtrasState()
        this.addEventListenersToInputs()
        this.initializeCategoryControls()
    }

    applyAvailabilityResponse(response) {
        if (this.descriptionElement && response.extras_description) {
            this.descriptionElement.textContent = response.extras_description
        }

        const hasPresentedExtras = Boolean(response.has_presented_extras)

        if (this.catalogContainer) {
            this.catalogContainer.classList.toggle('hidden', !hasPresentedExtras)
        }

        if (this.stageEmptyState) {
            this.stageEmptyState.classList.toggle('hidden', hasPresentedExtras)
        }

        if (this.catalogElement) {
            this.catalogElement.innerHTML = hasPresentedExtras
                ? (response.extras_catalog_html ?? '')
                : ''
        }

        this.extras = []
        this.initializeRenderedCatalog()
        this.mergeExtras(response.extras ?? [])
        this.applyExtraState()
        this.applyFiltersAndSorting()
    }

    applyBookingSummary(response = {}) {
        applySummaryBookingDetails(response.booking ?? {})
        applySummaryExtras(response.extras ?? [])
        applySummaryPrice(response.booking ?? {})
    }

    initializeCategoryControls() {
        this.categorySections.forEach((categorySection, index) => {
            const button = categorySection.querySelector('[data-extras-accordion-button]')
            const panel = categorySection.querySelector('[data-extras-accordion-panel]')

            if (!button || !panel) {
                return
            }

            const key = categorySection.dataset.categoryKey ?? `category-${index}`
            this.categoryExpandedState.set(key, button.getAttribute('aria-expanded') === 'true')

            button.addEventListener('click', () => {
                const isExpanded = !(this.categoryExpandedState.get(key) ?? false)
                this.categoryExpandedState.set(key, isExpanded)
                this.setCategoryExpandedState(categorySection, isExpanded)
            })
        })

        this.searchInput?.addEventListener('input', () => this.applyFiltersAndSorting())
        this.sortSelect?.addEventListener('change', () => this.applyFiltersAndSorting())

        this.applyFiltersAndSorting()
    }

    applyFiltersAndSorting() {
        if (this.categorySections.length === 0) {
            return
        }

        const searchTerm = this.searchInput?.value?.trim().toLowerCase() ?? ''
        const sortMode = this.sortSelect?.value ?? 'az'
        let visibleCategories = 0

        this.categorySections.forEach((categorySection, index) => {
            const options = Array.from(categorySection.querySelectorAll('[data-extra-option]'))
            const panel = categorySection.querySelector('[data-extras-accordion-panel]')
            const visibleOptions = []

            options.forEach((option) => {
                const matchesSearch = searchTerm === ''
                    || option.dataset.extraName?.includes(searchTerm)
                    || option.dataset.extraDescription?.includes(searchTerm)
                    || option.dataset.extraCode?.toLowerCase().includes(searchTerm)

                option.classList.toggle('hidden', !matchesSearch)

                if (matchesSearch) {
                    visibleOptions.push(option)
                }
            })

            this.sortOptions(visibleOptions, sortMode).forEach((option) => {
                panel?.appendChild(option)
            })

            const visibleCount = visibleOptions.length
            categorySection.classList.toggle('hidden', visibleCount === 0)
            categorySection.querySelector('[data-extras-category-count]').textContent = String(visibleCount)

            if (visibleCount > 0) {
                visibleCategories += 1
                const key = categorySection.dataset.categoryKey ?? `category-${index}`
                const expanded = searchTerm !== ''
                    ? true
                    : (this.categoryExpandedState.get(key) ?? index === 0)
                this.setCategoryExpandedState(categorySection, expanded)
            }
        })

        if (this.noResultsMessage) {
            this.noResultsMessage.classList.toggle('hidden', visibleCategories > 0)
        }
    }

    sortOptions(options, sortMode) {
        return [...options].sort((left, right) => {
            if (sortMode === 'price') {
                const priceDifference = parseFloat(left.dataset.extraPrice ?? '0') - parseFloat(right.dataset.extraPrice ?? '0')

                if (priceDifference !== 0) {
                    return priceDifference
                }
            }

            return (left.dataset.extraName ?? '').localeCompare(right.dataset.extraName ?? '')
        })
    }

    setCategoryExpandedState(categorySection, isExpanded) {
        const button = categorySection.querySelector('[data-extras-accordion-button]')
        const panel = categorySection.querySelector('[data-extras-accordion-panel]')

        if (!button || !panel) {
            return
        }

        button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false')
        panel.classList.toggle('hidden', !isExpanded)
    }
}

export default Extras
